<script>
   /***************************************************************
    * Range                                                       *
    * --------------------                                        *
    * control element for taking a numeric value from a range     *
    ***************************************************************/

   import { createEventDispatcher } from 'svelte';

   export let id;                                        // text ID for the input element
   export let value;                                     // initial value
   export let min;                                       // smallest value of the range
   export let max;                                       // largest value of the range
   export let decNum = 1;                                // number of decimals to show the current value
   export let step = +((max - min) / 100).toFixed(4);    // increment/decrement step
   export let discrete = false;                          // logical, if selected value must be shown as solo


   if (value < min || value > max) {
      throw new Error('The value is outside of the provided range.');
   }

   if (min > max) {
      throw new Error('The min and max values must be distinct.');
   }

   const dispatch = createEventDispatcher();

   let sliderElement;
   let sliderContainer;
   let isDragging = false;

   const computeValue = (p) => {
      const tmpValue = min + p * (max - min);

      // strange construction below is needed for:
      // a. make a value fractionated according to step
      // b. get rid of small decimals added by JS due to loss of precision
      return(+(Math.round(tmpValue / step) * step).toFixed(4));
   }

   /**
    * Returns relative position of the element 'e' inside the slider
    * @param e
    */
   const getRelativePosition = (e) => {
      const sliderRect = sliderElement.getBoundingClientRect();
      const parentRect = sliderContainer.getBoundingClientRect();
      const minX = sliderRect.x;
      const maxX = parentRect.x + parentRect.width;

      return (e.clientX - minX) / (maxX - minX);
   }

   /**
    * Handler of changing start event
    * @param e
    */
   const startChanging = (e) => {
      const p = getRelativePosition(e);
      if (p < 0 || p > 1) return;
      isDragging = p * 100 > width - 5 && p * 100 < width + 5;
   }

   /**
    * Handler of changing cancel event
    * @param e
    */
   const cancelChanging = (e) => {
      isDragging = false;
   }

   /**
    * Handler of changing stop event
    * @param e
    */
   const stopChanging = (e) => {
      isDragging = false;
      const p = getRelativePosition(e);
      if (p < 0 || p > 1) return;

      value = computeValue(p);
   }

   /**
    * Handler of event when changes are made by mouse wheel
    * @param e
    */
   const changingByWheel = (e) => {
      let newValue = value + step * e.deltaY * 0.5;
      if (newValue < min) newValue = min;
      if (newValue > max) newValue = max;

      value = +(Math.round(newValue / step) * step).toFixed(4);
   }

   /**
    * Handler of event when changes are made by dragging an element
    * @param e
    */
   const changing = (e) => {
      if (!isDragging) return;
      const p = getRelativePosition(e);
      if (p < 0 || p > 1) return;

      value = computeValue(p);
   }

   /**
    * Handler of event when changes are made by pressing left and right arrows
    * @param e
    */
   const changingByKeys = (e) => {
      if (e.key === 'ArrowLeft') {
         value = value - step < min ? min : value - step;
         return
      }

      if (e.key === 'ArrowRight') {
         value = value + step > max ? max : value + step;
         return
      }
   }

   $: width = min === max ? 100 : (value - min) / (max - min) * 100;
   $: dispatch('change', value);
</script>

<div
   id={id}
   role="slider"
   tabindex="0"
   aria-valuenow={value}
   aria-valuemin={min}
   aria-valuemax={max}
   class="range-slider-container"
   class:discrete={discrete}
   bind:this={sliderContainer}
   on:mousewheel|preventDefault={changingByWheel}
   on:mousemove={changing}
   on:keydown={changingByKeys}
   on:mousedown={startChanging}
   on:mouseleave={cancelChanging}
   on:mouseup={stopChanging}>

      {#if !discrete}
         <div class="range-slider" style="width:{width}%" bind:this={sliderElement}>
         {#if width > 25}
         <span style="padding-right:0.25em;color:#fefefe" class="range-value">{value.toFixed(decNum)}</span>
         {/if}
         </div>
         {#if width <= 25}
         <span style="padding-left:0.25em;color:#606060" class="range-value">{value.toFixed(decNum)}</span>
         {/if}
      {:else}
         <div class="range-slider" style="width:{width}%" bind:this={sliderElement}>
         {#if width >= 75}
         <span style="padding-right:0.25em;color:#606060" class="range-value">{value.toFixed(decNum)}</span>
         {/if}
         </div>
         {#if width < 75}
         <span style="padding-left:0.25em;color:#606060" class="range-value">{value.toFixed(decNum)}</span>
         {/if}
      {/if}
</div>


<style>
   .range-slider-container {
      position: relative;
      box-sizing: border-box;
      display: inline-block;
      flex: 1 1 auto;
      background: #f0f0f0;
      height: 1.5em;
      margin: 0;
      padding: 1px 0;
      width: auto;
      border-radius: 2px;
      cursor: default;

      display: flex;
      align-items: center;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
   }

   .range-slider {
      position: relative;
      display: flex;
      justify-content: right;
      align-items: center;
      box-sizing: border-box;
      border-radius: 2px;
      text-align: right;
      color: #fafafa;
      margin: 0;
      padding: 0;
      height: 100%;
      cursor:default;
      background: #6EB8FF;
      border-right: 5px solid #528abe;
      cursor: default;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
   }

   .range-value {
      display: inline-block;
      margin: 0;
      padding: 0;
      cursor: default;
      font-size: 0.85em;


      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
   }

   .range-slider-container.discrete > .range-slider {
      background: transparent;
      border-right: 8px solid #528abe;
      border-radius: 0;
   }


</style>

